import AvatarTool from './AvatarTool.js';
import { AvatarEvents } from './AvatarEvents.js';
import { computeAngle } from '../Minimap3D/MinimapMath.js';
import './Avatar.scss'; // IMPORTANT!!

const namespace = AutodeskNamespace('Autodesk.AEC');
const GlobalManagerMixin = Autodesk.Viewing.GlobalManagerMixin;
const av = Autodesk.Viewing;

export default class AvatarWidget extends Autodesk.Viewing.EventDispatcher {
    /**
     *
     * @param {HTMLElement} container - Div element where the avatar should be located.
     * @param {Viewer3D} targetViewer - Target viewer with a 3D model.
     * @param {GlobalManager} globalManager - Global manager.
     * @param {object} transforms - MUST contain these callbacks:
     *                                      mapPixelsToWorld
     *                                      worldToMapPixels
     *                                      mapPixelsToWidget
     *                                      widgetToMapPixels
     *                                      getMapRotationAngle
     * @param {HTMLElement} canvasWrap - Optional DOM element to catch events that are not consumed by the widget.
     *
     */
    constructor(container, targetViewer, globalManager, transforms, trackUsage, canvasWrap) {
        super();
        this.container = container;
        this.targetViewer = targetViewer;
        this.transforms = transforms;
        this.trackUsage = trackUsage;
        this.canvasWrap = canvasWrap;
        this.setGlobalManager(globalManager);

        this.widgetUI = this.createUI();

        this.tool = new AvatarTool(this);
        this.tool.activate();
    }

    createUI() {
        const _document = this.getDocument();

        const widget = _document.createElement('div');
        widget.className = 'avatar-widget';

        this.container.appendChild(widget);

        // Sub-widget to represent view direction
        const frustumWidget = _document.createElement('div');
        frustumWidget.className = 'avatar-frustum';
        widget.appendChild(frustumWidget);
        widget.frustumWidget = frustumWidget;

        // By default, setting left/top to posX/posY would move the top-left corner of the frustum
        // widget to that position. We want to have the tip of the camera there.
        // Note that the offset may change depending on the border sizes in avatar-frustum css class.
        widget.anchorX = 20;  // =.avatar-frustum.border-left
        widget.anchorY = 40;  // =.avatar-frustum.border-top

        // Filled circle at camera position
        const filledCircle = _document.createElement('div');
        filledCircle.className = 'avatar-position-fill';
        widget.appendChild(filledCircle);
        widget.posCircle = filledCircle;

        // Hit area of the avatar position.
        const hitArea = _document.createElement('div');
        hitArea.className = 'avatar-position-hitarea';
        widget.posCircle.appendChild(hitArea);
        widget.hitArea = hitArea;

        if (av.isTouchDevice()) {
            hitArea.classList.add('touch');
            frustumWidget.classList.add('touch');
            widget.posCircle.classList.add('touch');
            widget.anchorX = 40;  // =.avatar-frustum.border-left
            widget.anchorY = 80;  // =.avatar-frustum.border-top
        }

        return widget;
    }

    destroy() {
        this.tool.deactivate();
        this.tool = null;

        this.container.removeChild(this.widgetUI);
        this.widgetUI = null;
    }

    // Show avatar - resolve after animation ends.
    show() {
        return this.changeVisibility(true);
    }

    // Hide avatar - resolve after animation ends.
    hide() {
        return this.changeVisibility(false);
    }

    // Change avatar visibility if needed - resolve after animation ends.
    changeVisibility(show) {
        return new Promise(resolve => {
            if (show ^ this.widgetUI.classList.contains('visible')) {
                show ? this.widgetUI.classList.add("visible") : this.widgetUI.classList.remove("visible");

                const onTransitionEnd = () => {
                    this.widgetUI.removeEventListener('transitionend', onTransitionEnd);
                    resolve();
                };

                this.widgetUI.addEventListener('transitionend',  onTransitionEnd);
            } else {
                resolve();
            }
        });
    }

    // get camera position in map pixel coords
    getPosition2D() {
        const pos3D = this.targetViewer.impl.camera.position;
        return this.transforms.worldToMapPixels(pos3D);
    }

    // get camera target in map pixel coords
    getTarget2D() {
        const target3D = this.targetViewer.impl.camera.target;
        return this.transforms.worldToMapPixels(target3D);
    }

    // Update the avatar position and rotation, according to the targetViewer's camera.
    update() {
        // get position and target in pixels within the map
        const pos2D    = this.getPosition2D();
        const target2D = this.getTarget2D();

        const playerAngle = computeAngle(pos2D, target2D);

        // rotate frustum shape (+ consider map rotation)
        const mapRotationAngle = this.transforms.getMapRotationAngle();
        this.widgetUI.frustumWidget.style.transform = `rotate(${playerAngle + mapRotationAngle}rad)`;

        this.transforms.mapPixelsToWidget(pos2D);

        this.widgetUI.style.transform = `translate(${pos2D.x - this.widgetUI.anchorX}px, ${pos2D.y - this.widgetUI.anchorY}px)`;
    }

    isDragging() {
        return this.tool.isDragging;
    }

    isRotating() {
        return this.tool.isRotating;
    }
}

GlobalManagerMixin.call(AvatarWidget.prototype);
Object.assign(namespace, { AvatarEvents });
